import isString from 'lodash/isString';
import sortBy from 'lodash/sortBy';
import { parse, stringify } from 'qs';
import { Environment, LOCALHOST } from 'utils/Environment';

export enum LanguageCode {
  ENGLISH = 'en',
  FRENCH = 'fr',
  SPANISH = 'es',
  GERMAN = 'de',
  DUTCH = 'nl',
  PORTUGUESE = 'pt',
}

export const DEFAULT_LANGUAGE_CODE = LanguageCode.ENGLISH;

export enum CountryCode {
  ARGENTINA = 'AR',
  AUSTRALIA = 'AU',
  AUSTRIA = 'AT',
  BRAZIL = 'BR',
  CANADA = 'CA',
  GERMANY = 'DE',
  IRELAND = 'IE',
  MEXICO = 'MX',
  NETHERLANDS = 'NL',
  UINITED_KINGDOM = 'GB',
  UNITED_STATES = 'US',
}

export enum DataCenterCode {
  NORTH_AMERICA = 'NA',
  LATIN_AMERICA = 'LA',
  EUROPE = 'EU',
  SOUTH_PACIFIC = 'APAC',
}

export interface IDataCenter {
  displayName: string;
  code: DataCenterCode;
  prodHost: string;
  devHost: string;
  stgHost: string;
}

export const supportedDataCenters: Readonly<Record<DataCenterCode, IDataCenter>> = {
  [DataCenterCode.NORTH_AMERICA]: {
    displayName: 'North America',
    code: DataCenterCode.NORTH_AMERICA,
    prodHost: 'www.clover.com',
    devHost: 'dev1.dev.clover.com',
    stgHost: 'www.staging.clover.com',
  },
  [DataCenterCode.EUROPE]: {
    displayName: 'Europe',
    code: DataCenterCode.EUROPE,
    prodHost: 'www.eu.clover.com',
    devHost: 'demo2.dev.clover.com',
    stgHost: 'stg3.dev.clover.com',
  },
  [DataCenterCode.LATIN_AMERICA]: {
    displayName: 'Latin America',
    code: DataCenterCode.LATIN_AMERICA,
    prodHost: 'prodHost',
    devHost: 'dev7.dev.clover.com',
    stgHost: 'stg2.dev.clover.com',
  },
  [DataCenterCode.SOUTH_PACIFIC]: {
    displayName: 'South Pacific',
    code: DataCenterCode.SOUTH_PACIFIC,
    prodHost: 'www.clover.com',
    devHost: 'dev2.dev.clover.com',
    stgHost: 'www.ap.staging.clover.com',
  },
};

export const hostByCountryCode: Partial<Record<string, Partial<Record<CountryCode, string>>>> = {
  prodHost: {
    [CountryCode.ARGENTINA]: 'ar.clover.com',
    [CountryCode.BRAZIL]: 'br.clover.com',
    [CountryCode.MEXICO]: 'mex.clover.com',
  },
};

export interface ILanguage {
  readonly displayName: string;
  readonly code: LanguageCode;
}

export interface ICountry {
  readonly displayName: Partial<Record<LanguageCode, string>>;
  readonly code: CountryCode;
  readonly languages: ILanguage[];
  readonly dataCenter: IDataCenter;
  readonly icon: string;
  readonly subPathOverride?: string; // Example: '/ca'
}

/**
 * ISO language codes.
 * Left side of the hyphen is the language code and right side is the country code.
 * @example 'en-US', 'fr-CA', 'es-AR'
 */
type LocaleCode = string;

export class Locale {
  readonly displayName: string;
  readonly code: LocaleCode;
  readonly country: Readonly<ICountry>;
  readonly languageCode: LanguageCode;

  constructor(lang: ILanguage, country: ICountry) {
    if (!country.languages.find(l => l.code.toLowerCase() === lang.code.toLowerCase())) {
      throw new Error(
        `Attempting to initialize locale with an invalid combination of ${lang.code}-${country.code}`,
      );
    }

    this.displayName = `${
      country.displayName[lang.code.toLowerCase() as LanguageCode] ||
      country.displayName[DEFAULT_LANGUAGE_CODE]
    } (${lang.displayName})`;
    this.code = `${lang.code.toLowerCase()}-${country.code.toUpperCase()}`;
    this.country = country;
    this.languageCode = lang.code;
  }

  isValidInDataCenter(dc: DataCenterCode | IDataCenter = getDataCenter()): boolean {
    if (!dc) return false;

    const dataCenterCode = isString(dc) ? dc : dc.code;
    return this.country.dataCenter.code === dataCenterCode;
  }
}

export const supportedLanguages: Readonly<Record<LanguageCode, ILanguage>> = {
  [LanguageCode.ENGLISH]: {
    displayName: 'English',
    code: LanguageCode.ENGLISH,
  },
  [LanguageCode.FRENCH]: {
    displayName: 'Français',
    code: LanguageCode.FRENCH,
  },
  [LanguageCode.SPANISH]: {
    displayName: 'Español',
    code: LanguageCode.SPANISH,
  },
  [LanguageCode.GERMAN]: {
    displayName: 'Deutsch',
    code: LanguageCode.GERMAN,
  },
  [LanguageCode.DUTCH]: {
    displayName: 'Nederlands',
    code: LanguageCode.DUTCH,
  },
  [LanguageCode.PORTUGUESE]: {
    displayName: 'Português',
    code: LanguageCode.PORTUGUESE,
  },
};

import AR_FLAG from 'assets/flags/argentina.svg';
import AT_FLAG from 'assets/flags/austria.svg';
import AU_FLAG from 'assets/flags/australia.svg';
import BR_FLAG from 'assets/flags/brazil.svg';
import CA_FLAG from 'assets/flags/canada.svg';
import DE_FLAG from 'assets/flags/germany.svg';
import GB_FLAG from 'assets/flags/united-kingdom.svg';
import IE_FLAG from 'assets/flags/ireland.svg';
import MX_FLAG from 'assets/flags/mexico.svg';
import NL_FLAG from 'assets/flags/netherlands.svg';
import US_FLAG from 'assets/flags/united-states.svg';
import { useRoutesByDataCenter } from './AppRoute';
import { ensureNoTrailingSlash } from './Urls';

export const supportedCountries: Readonly<Record<CountryCode, ICountry>> = {
  [CountryCode.UNITED_STATES]: {
    displayName: { en: 'United States', es: 'Estados Unidos' },
    code: CountryCode.UNITED_STATES,
    languages: [supportedLanguages.en],
    dataCenter: supportedDataCenters[DataCenterCode.NORTH_AMERICA],
    icon: US_FLAG,
  },
  [CountryCode.CANADA]: {
    displayName: { en: 'Canada', fr: 'Canada' },
    code: CountryCode.CANADA,
    languages: [supportedLanguages.en, supportedLanguages.fr],
    dataCenter: supportedDataCenters[DataCenterCode.NORTH_AMERICA],
    subPathOverride: '/ca',
    icon: CA_FLAG,
  },
  [CountryCode.ARGENTINA]: {
    displayName: { en: 'Argentina', es: 'Argentina' },
    code: CountryCode.ARGENTINA,
    languages: [supportedLanguages.es],
    dataCenter: supportedDataCenters[DataCenterCode.LATIN_AMERICA],
    icon: AR_FLAG,
  },
  [CountryCode.BRAZIL]: {
    displayName: { en: 'Brazil', pt: 'Brasil' },
    code: CountryCode.BRAZIL,
    languages: [supportedLanguages.pt],
    dataCenter: supportedDataCenters[DataCenterCode.LATIN_AMERICA],
    icon: BR_FLAG,
  },
  [CountryCode.MEXICO]: {
    displayName: { en: 'Mexico', es: 'México' },
    code: CountryCode.MEXICO,
    languages: [supportedLanguages.es],
    dataCenter: supportedDataCenters[DataCenterCode.LATIN_AMERICA],
    icon: MX_FLAG,
  },
  [CountryCode.UINITED_KINGDOM]: {
    displayName: { en: 'United Kingdom' },
    code: CountryCode.UINITED_KINGDOM,
    languages: [supportedLanguages.en],
    dataCenter: supportedDataCenters[DataCenterCode.EUROPE],
    icon: GB_FLAG,
  },
  [CountryCode.GERMANY]: {
    displayName: { en: 'Germany', de: 'Deutschland' },
    code: CountryCode.GERMANY,
    languages: [supportedLanguages.de],
    dataCenter: supportedDataCenters[DataCenterCode.EUROPE],
    icon: DE_FLAG,
  },
  [CountryCode.AUSTRIA]: {
    displayName: { en: 'Austria', de: 'Österreich' },
    code: CountryCode.AUSTRIA,
    languages: [supportedLanguages.de],
    dataCenter: supportedDataCenters[DataCenterCode.EUROPE],
    icon: AT_FLAG,
  },
  [CountryCode.IRELAND]: {
    displayName: { en: 'Ireland' },
    code: CountryCode.IRELAND,
    languages: [supportedLanguages.en],
    dataCenter: supportedDataCenters[DataCenterCode.EUROPE],
    icon: IE_FLAG,
  },
  [CountryCode.NETHERLANDS]: {
    displayName: { en: 'Netherlands', nl: 'Nederland' },
    code: CountryCode.NETHERLANDS,
    languages: [supportedLanguages.en, supportedLanguages.nl],
    dataCenter: supportedDataCenters[DataCenterCode.EUROPE],
    icon: NL_FLAG,
  },
  [CountryCode.AUSTRALIA]: {
    displayName: { en: 'Australia', es: 'Australia' },
    code: CountryCode.AUSTRALIA,
    languages: [supportedLanguages.en],
    dataCenter: supportedDataCenters[DataCenterCode.SOUTH_PACIFIC],
    subPathOverride: '/au',
    icon: AU_FLAG,
  },
};

// All supported locales for all data centers
export const supportedLocales: Readonly<Record<LocaleCode, Locale>> = Object.values(supportedCountries)
  .map(c => c.languages.map(l => new Locale(l, c)))
  .reduce((map, localeArray) => {
    localeArray.forEach(locale => (map[locale.code] = locale));
    return map;
  }, {} as Record<LocaleCode, Locale>);

// All supported locales for this app
export const supportedAppLocales = Object.values(supportedLocales).filter(
  l => l.country.code === CountryCode.UNITED_STATES || l.country.subPathOverride,
);

/**
 * Gets all locales within the given data center in alphabetical order.
 */
export function getLocalesForDataCenter(dc: DataCenterCode | IDataCenter = getDataCenter()): Locale[] {
  const dataCenterCode = isString(dc) ? dc : dc.code;
  return sortBy(
    Object.values(supportedLocales).filter(locale => locale.country.dataCenter.code === dataCenterCode),
    (locale: Locale) => locale.displayName,
  );
}

export const localeCodes = Object.keys(supportedLocales);

export const DEFAULT_LOCALE = supportedLocales['en-US'];

export const getLocale = (localeCode: LocaleCode): Locale => {
  return supportedLocales[localeCode] || getDefaultLocale();
};

/**
 * The below class can be treated as a base type for both lib.dom.d.ts:Location or lib.dom.d.ts:URL.
 */
type URL = {
  hash: string;
  host: string;
  hostname: string;
  href: string;
  readonly origin: string;
  pathname: string;
  port: string;
  protocol: string;
  search: string;
};

/**
 * Generates an absolute url for the given location object in the correct data center for the locale.
 * NOTE: Not meant to be used for linking within the same data center because it strips out the current pathname.
 */
export function generateUrl(locale: Locale, loc: URL = window.location): string {
  const { host, search = '', hash = '' } = loc;
  const { lng, ...existingQueryParams } = parse(search, { ignoreQueryPrefix: true }) || {};
  const { subPathOverride, dataCenter } = locale.country;
  const isAppSupported = supportedAppLocales.some(l => l.code === locale.code);
  const newQueryParams = {
    ...existingQueryParams,
    ...(!isAppSupported && { lng: locale.code }),
  };
  const isLocalhost = Environment.current.isLocalhost || LOCALHOST.test(host.split('.')[0]);
  let protocol = 'https:';
  let currentEnvHost;

  if (isLocalhost) {
    currentEnvHost = dataCenter.devHost;
    if (isAppSupported) {
      currentEnvHost = host;
      protocol = 'http:';
    }
  } else {
    currentEnvHost = getHost(locale);
  }

  const newHost = ensureNoTrailingSlash(currentEnvHost);

  const isPrimaryLanguage = locale.country.languages.findIndex(l => l.code === locale.languageCode) === 0;
  const languagePath = !isPrimaryLanguage ? `/${locale.languageCode}` : '';
  const newPathname = subPathOverride ? `${ensureNoTrailingSlash(`${subPathOverride}${languagePath}`)}` : '';

  return `${protocol}//${newHost}${newPathname}${stringify(newQueryParams, { addQueryPrefix: true })}${hash}`;
}

export function getDataCenter(
  { host }: URL = window.location,
  isNonProd: boolean = Environment.current.isDev,
): IDataCenter {
  if (!isNonProd) {
    return Object.values(supportedDataCenters).find(dc => dc.prodHost === host) || supportedDataCenters.NA;
  } else if (Environment.current.isCloverStagingEnvironment) {
    return Object.values(supportedDataCenters).find(dc => dc.stgHost === host) || supportedDataCenters.NA;
  } else {
    return Object.values(supportedDataCenters).find(dc => dc.devHost === host) || supportedDataCenters.NA;
  }
}

/**
 * Checks if the given locale is a valid locale code and is valid for the data center.
 * Case sensitive meaning en-us is considered invalid.
 */
export function isValidLocale(locale: LocaleCode | Locale): boolean {
  if (!locale) return false;

  const localeCode = isString(locale) ? locale : locale.code;

  return supportedAppLocales.some(l => l.code === localeCode);
}

/**
 * Given a data center and language will try and determine the best default locale.
 */
export function getDefaultLocale(
  lang: LanguageCode = LanguageCode.ENGLISH,
  dc: IDataCenter | DataCenterCode = getDataCenter(),
): Locale {
  const dataCenterCode = isString(dc) ? dc : dc.code;
  const validLocales = getLocalesForDataCenter(dataCenterCode);
  const isDefaultDataCenter = DEFAULT_LOCALE.isValidInDataCenter(dc);

  // Try prioritize DEFAULT_LOCALE if it is the same language
  if (isDefaultDataCenter && DEFAULT_LOCALE.code.startsWith(lang)) {
    return DEFAULT_LOCALE;
  }

  // Get the first locale that includes this language
  const locale: Locale | undefined = validLocales.find(locale => locale.code.startsWith(lang.toLowerCase()));

  // If there are no locales that include this language then try prioritizing DEFAULT_LOCALE again
  if (isDefaultDataCenter) return DEFAULT_LOCALE;

  return locale || validLocales[0] || DEFAULT_LOCALE;
}

// Only use full locale codes for 18next
export const i18nLanguages = new Set<string>([...Object.values(supportedLocales).map(l => l.code)]);

export const ALL_LOCALES_FOR_DATA_CENTER = Object.values(supportedLocales);

/**
 * Returns the current locale based on the URL.
 * If the locale is not found in the URL, it will return the default locale.
 * NOTE: use this function if you are outside the react component tree.
 */
export const getCurrentLocaleFromURL = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [routeToLocale] = useRoutesByDataCenter(ALL_LOCALES_FOR_DATA_CENTER);
  const currentLocaleRoute = Object.keys(routeToLocale).find(key => {
    const { pathname } = window.location;
    return key === pathname || (pathname.startsWith(key) && pathname.substring(key.length).charAt(0) === '/');
  });

  return currentLocaleRoute ? routeToLocale[currentLocaleRoute] : DEFAULT_LOCALE;
};

/**
 * Returns host from data center based on the environment.
 * @param dataCenter - DataCenter. Default is from NA data center.
 * @returns {String} - Host based on the environment.
 */
export function getHost(locale = DEFAULT_LOCALE) {
  const { isCloverDevEnvironment, isCloverStagingEnvironment, isDevHost, isLocalhost } = Environment.current;

  let host = locale.country.dataCenter.prodHost;

  if (isCloverDevEnvironment || isDevHost || isLocalhost) {
    host = locale.country.dataCenter.devHost;
  } else if (isCloverStagingEnvironment) {
    host = locale.country.dataCenter.stgHost;
  }

  const customHost = hostByCountryCode[host]?.[locale.country.code];
  return customHost || host;
}
